import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DiJira, DiTrello } from 'react-icons/di';
import { LuArrowLeft, LuCircleCheck, LuSparkles } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { ProviderId } from '../entities/provider';
import { WizardProposal } from '../entities/wizard-proposal';
import {
  confirmJiraProjects,
  fetchIntegrations,
  fetchUserProjects,
  getJiraAuthUrl,
  getTrelloAuthUrl,
  UserProjectsData,
} from '../shared/api/integration';
import { listStandaloneInvitations } from '../shared/api/standalone';
import { getStepLabel, usePipelineEvents } from '../shared/hooks/use-pipeline-events';
import { DID_YOU_KNOW, useRotatingTip } from '../shared/hooks/use-rotating-tip';
import WizardPanel from './chat/WizardPanel';

export const ONBOARDING_DONE_KEY = 'mflow_onboarding_done';
export const ASK_AI_HIGHLIGHT_KEY = 'mflow_ask_ai_highlight';

const SYNC_PRESETS = [
  { label: 'Last month', months: 1 },
  { label: 'Last 3 months', months: 3 },
  { label: 'Last 6 months', months: 6 },
  { label: 'Last year', months: 12 },
  { label: 'Last 2 years', months: 24 },
] as const;

const syncFromDate = (months: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

type Step =
  | 'platform' // choose Jira / Trello / Standalone, or skip
  | 'jira-choice' // Jira connected — sync existing projects, or start a new spec
  | 'jira-projects' // existing "pick projects to sync" flow, unchanged
  | 'jira-syncing'
  | 'trello-choice' // Trello connected — sync existing boards, or start a new spec
  | 'trello-syncing'
  | 'wizard' // describe a spec, review proposal, pick a platform, build
  | 'done';

const PHASE_LABEL: Record<Step, string> = {
  platform: 'Get started',
  'jira-choice': 'Jira connected',
  'jira-projects': 'Select projects',
  'jira-syncing': 'Syncing',
  'trello-choice': 'Trello connected',
  'trello-syncing': 'Syncing',
  wizard: 'Describe your project',
  done: 'All set',
};

const PlatformCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  loading?: boolean;
}> = ({ icon, title, description, onClick, loading }) => (
  <button
    className="flex flex-col items-start gap-2 rounded-xl bg-base-200 shadow-card hover:bg-base-300 hover:-translate-y-0.5 transition p-5 text-left disabled:opacity-60 disabled:translate-y-0"
    onClick={onClick}
    disabled={loading}
  >
    <div className="text-3xl">{loading ? <span className="loading loading-spinner loading-md" /> : icon}</div>
    <div className="font-semibold">{title}</div>
    <div className="text-sm opacity-60">{description}</div>
  </button>
);

const ChoiceCard: React.FC<{ title: string; description: string; onClick: () => void }> = ({
  title,
  description,
  onClick,
}) => (
  <button
    className="flex flex-col items-start gap-1.5 rounded-xl bg-base-200 shadow-card hover:bg-base-300 hover:-translate-y-0.5 transition p-5 text-left"
    onClick={onClick}
  >
    <div className="font-semibold">{title}</div>
    <div className="text-sm opacity-60">{description}</div>
  </button>
);

const STEP_ORDER: Step[] = ['platform', 'jira-projects', 'jira-syncing', 'wizard', 'done'];

const StepProgress: React.FC<{ step: Step }> = ({ step }) => {
  // Collapse the choice/syncing variants onto one shared position each, so the strip
  // reads as one linear progression regardless of which platform branch was taken.
  const normalized =
    step === 'jira-choice' || step === 'trello-choice' ? 'platform' : step === 'trello-syncing' ? 'jira-syncing' : step;
  const index = STEP_ORDER.indexOf(normalized);
  return (
    <div className="flex gap-1">
      {STEP_ORDER.map((s, i) => (
        <div
          key={s}
          className={`h-1 w-6 rounded-full transition-colors ${i <= index ? 'bg-primary' : 'bg-base-300'}`}
        />
      ))}
    </div>
  );
};

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<Step>('platform');
  const [platform, setPlatform] = useState<ProviderId | undefined>(undefined);
  const [builtProposal, setBuiltProposal] = useState<WizardProposal | null>(null);

  const [isConnectingJira, setIsConnectingJira] = useState(false);
  const [isConnectingTrello, setIsConnectingTrello] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const [userProjects, setUserProjects] = useState<UserProjectsData | null>(null);
  const [pendingSelected, setPendingSelected] = useState<Set<string>>(new Set());
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncPreset, setSyncPreset] = useState<number>(12);

  const { runs } = usePipelineEvents();
  const { tip, tipIndex, tipVisible } = useRotatingTip(step === 'jira-syncing' || step === 'trello-syncing');

  useEffect(() => {
    if (localStorage.getItem(ONBOARDING_DONE_KEY)) return;

    // A pending Standalone invitation (e.g. a brand-new signup arriving from an invite email,
    // which links to the app's base URL, not a deep link) takes priority over this full-screen
    // flow — showing it here would otherwise bury the invitation behind an unrelated "connect
    // Jira/Trello" prompt the invited user has no reason to care about. Skip for this load only;
    // ONBOARDING_DONE_KEY is deliberately left unset, so it still shows normally on a later visit
    // once the invitation's been resolved. Defaults to "no invitations" on its own failure (e.g.
    // the endpoint isn't deployed yet) so a broken/absent check never suppresses onboarding for
    // everyone else — only fetchIntegrations() failing does that, matching prior behavior.
    listStandaloneInvitations()
      .catch(() => [])
      .then((invitations) => {
        if (invitations.length > 0) return;
        return fetchIntegrations().then((data) => {
          if (data?.isJiraConnected) {
            setStep('jira-choice');
          } else if (data?.isTrelloConnected) {
            setStep('trello-choice');
          } else {
            setStep('platform');
          }
          setVisible(true);
        });
      })
      .catch(() => {
        // don't block the app on failure
      });
  }, []);

  useEffect(() => {
    if (step !== 'jira-syncing' && step !== 'trello-syncing') return;
    const relevantRuns = runs.filter((r) =>
      step === 'jira-syncing'
        ? r.pipelineId.toLowerCase().includes('jira')
        : r.pipelineId.toLowerCase().includes('trello'),
    );
    if (relevantRuns.some((r) => r.status === 'completed')) {
      setStep('done');
    }
  }, [runs, step]);

  const enterProjectsStep = () => {
    setIsLoadingProjects(true);
    fetchUserProjects()
      .then((d) => {
        setUserProjects(d);
        setPendingSelected(new Set(d.selectedProjectIds));
      })
      .catch(() => {})
      .finally(() => setIsLoadingProjects(false));
    setStep('jira-projects');
  };

  const handleConnectJira = async () => {
    setConnectError(null);
    setIsConnectingJira(true);
    try {
      const { url } = await getJiraAuthUrl();
      if (!url) throw new Error('No auth URL returned');

      localStorage.setItem('jira_connect_mode', 'stepper');

      const popupWidth = 600;
      const popupHeight = 600;
      const left = window.innerWidth / 2 - popupWidth / 2;
      const top = window.innerHeight / 2 - popupHeight / 2;
      const popup = window.open(
        url,
        'jira-auth-popup',
        `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`,
      );

      if (popup) {
        const onMessage = (e: MessageEvent) => {
          if (e.data?.type === 'jira-connected') {
            window.removeEventListener('message', onMessage);
            clearInterval(poll);
            setStep('jira-choice');
          }
        };
        window.addEventListener('message', onMessage);
        const poll = setInterval(() => {
          if (popup.closed) {
            clearInterval(poll);
            window.removeEventListener('message', onMessage);
            localStorage.removeItem('jira_connect_mode');
          }
        }, 1000);
      }
    } catch {
      setConnectError('Failed to open Jira authorization. Please try again.');
    } finally {
      setIsConnectingJira(false);
    }
  };

  const handleConnectTrello = async () => {
    setConnectError(null);
    setIsConnectingTrello(true);
    try {
      const { url } = await getTrelloAuthUrl();
      if (!url) throw new Error('No auth URL returned');

      const popupWidth = 600;
      const popupHeight = 600;
      const left = window.innerWidth / 2 - popupWidth / 2;
      const top = window.innerHeight / 2 - popupHeight / 2;
      const popup = window.open(
        url,
        'trello-auth-popup',
        `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`,
      );

      if (popup) {
        const onMessage = (e: MessageEvent) => {
          if (e.data?.type === 'trello-connected') {
            window.removeEventListener('message', onMessage);
            clearInterval(poll);
            setStep('trello-choice');
          }
        };
        window.addEventListener('message', onMessage);
        const poll = setInterval(() => {
          if (popup.closed) {
            clearInterval(poll);
            window.removeEventListener('message', onMessage);
          }
        }, 1000);
      }
    } catch {
      setConnectError('Failed to open Trello authorization. Please try again.');
    } finally {
      setIsConnectingTrello(false);
    }
  };

  const handleStartSync = async () => {
    setIsSyncing(true);
    try {
      await confirmJiraProjects(Array.from(pendingSelected), syncFromDate(syncPreset));
      setStep('jira-syncing');
    } catch {
      // stay on projects step; user can retry
    } finally {
      setIsSyncing(false);
    }
  };

  const dismiss = (highlightAskAi = false) => {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1');
    if (highlightAskAi) localStorage.setItem(ASK_AI_HIGHLIGHT_KEY, '1');
    setVisible(false);
  };

  const toggleProject = (id: string) => {
    setPendingSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const goToChat = () => {
    dismiss();
    navigate('/chat');
  };

  const goToDashboard = () => {
    dismiss(true);
    window.location.href = '/';
  };

  if (!visible) return null;

  const overLimit = userProjects?.planLimit != null && pendingSelected.size > userProjects.planLimit;
  const activeRun = runs.find((r) => r.status === 'running');
  const activeStepId = activeRun?.steps.find((s) => s.status === 'running')?.stepId;

  const canGoBackToPlatform = step === 'jira-choice' || step === 'trello-choice';

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-base-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-base-300">
        <span className="font-display font-bold text-lg tracking-wide">MFlow</span>
        <div className="flex items-center gap-3">
          {canGoBackToPlatform && (
            <button className="btn btn-ghost btn-xs btn-circle" onClick={() => setStep('platform')} title="Back">
              <LuArrowLeft size={14} />
            </button>
          )}
          <StepProgress step={step} />
          <span className="text-[10px] uppercase tracking-widest opacity-40">{PHASE_LABEL[step]}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={clsx('w-full space-y-8', step === 'wizard' ? 'max-w-4xl' : 'max-w-lg')}
          >
            {/* Step: platform */}
            {step === 'platform' && (
              <>
                <div className="space-y-3">
                  <h1 className="text-3xl font-bold font-display">Where do you want to track this?</h1>
                  <p className="text-base opacity-60 leading-relaxed max-w-md">
                    Connect an existing tool to sync real history, or describe a new project and let mFlow set it up for
                    you.
                  </p>
                </div>
                {connectError && (
                  <div className="alert alert-error text-sm py-2">
                    <span>{connectError}</span>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-3">
                  <PlatformCard
                    icon={<DiJira className="text-blue-500" />}
                    title="Jira"
                    description="Sync existing projects or start a new one"
                    onClick={handleConnectJira}
                    loading={isConnectingJira}
                  />
                  <PlatformCard
                    icon={<DiTrello className="text-sky-500" />}
                    title="Trello"
                    description="Sync existing boards or start a new one"
                    onClick={handleConnectTrello}
                    loading={isConnectingTrello}
                  />
                  <PlatformCard
                    icon={<LuSparkles className="text-primary" />}
                    title="Standalone"
                    description="No setup — mFlow tracks it for you"
                    onClick={() => {
                      setPlatform('standalone');
                      setStep('wizard');
                    }}
                  />
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setStep('wizard')}>
                  Skip — I&apos;ll decide later
                </button>
              </>
            )}

            {/* Step: jira-choice / trello-choice */}
            {(step === 'jira-choice' || step === 'trello-choice') && (
              <>
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold font-display">
                    {step === 'jira-choice' ? 'Jira connected' : 'Trello connected'}
                  </h1>
                  <p className="text-base opacity-60">What would you like to do first?</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ChoiceCard
                    title={step === 'jira-choice' ? 'Sync my existing projects' : 'Sync my existing boards'}
                    description="Pull in real history to compute delivery metrics and risk analysis."
                    onClick={step === 'jira-choice' ? enterProjectsStep : () => setStep('trello-syncing')}
                  />
                  <ChoiceCard
                    title="Start a new project from a spec"
                    description="Describe what you're building — mFlow proposes a plan and creates it for you."
                    onClick={() => {
                      setPlatform(step === 'jira-choice' ? 'jira' : 'trello');
                      setStep('wizard');
                    }}
                  />
                </div>
              </>
            )}

            {/* Step: jira-projects (unchanged from before) */}
            {step === 'jira-projects' && (
              <>
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold font-display">Select projects to track</h1>
                  <p className="text-base opacity-60">Choose which Jira projects MFlow should sync and analyze.</p>
                </div>

                {isLoadingProjects ? (
                  <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg" />
                  </div>
                ) : userProjects ? (
                  userProjects.availableProjects?.length === 0 ? (
                    <div className="space-y-4">
                      <div className="rounded-xl bg-base-200 shadow-card p-8 flex flex-col items-center text-center gap-3">
                        <DiJira className="text-5xl opacity-30" />
                        <div>
                          <p className="font-semibold">No projects found</p>
                          <p className="text-sm opacity-60 mt-1 max-w-xs">
                            Your Jira account doesn&apos;t have any accessible projects. Make sure you have at least
                            Browse Projects permission in Jira, then reconnect.
                          </p>
                        </div>
                        <button
                          className="btn btn-outline btn-sm mt-1"
                          onClick={() => {
                            setUserProjects(null);
                            setStep('platform');
                          }}
                        >
                          Reconnect Jira
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {userProjects.planLimit != null && (
                        <div className={`text-sm ${overLimit ? 'text-error font-medium' : 'opacity-60'}`}>
                          {overLimit
                            ? `Plan limit: max ${userProjects.planLimit}. Deselect ${pendingSelected.size - userProjects.planLimit!}.`
                            : `${userProjects.plan} plan — up to ${userProjects.planLimit} project${userProjects.planLimit === 1 ? '' : 's'}`}
                        </div>
                      )}
                      <div className="rounded-xl bg-base-200 shadow-card p-3 space-y-1 max-h-80 overflow-y-auto">
                        {userProjects.availableProjects.map((p) => (
                          <label
                            key={p.id}
                            className="flex items-center gap-3 cursor-pointer rounded-lg hover:bg-base-300 px-3 py-2.5 transition-colors"
                          >
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={pendingSelected.has(p.id)}
                              onChange={() => toggleProject(p.id)}
                            />
                            <span className="font-medium flex-1">{p.name}</span>
                            <span className="font-mono text-xs opacity-40">{p.key}</span>
                          </label>
                        ))}
                      </div>

                      <div className="space-y-1.5 pt-1">
                        <label htmlFor="onboarding-sync-from" className="text-sm font-medium">
                          Sync history from
                        </label>
                        <select
                          id="onboarding-sync-from"
                          className="select select-bordered select-sm w-full"
                          value={syncPreset}
                          onChange={(e) => setSyncPreset(Number(e.target.value))}
                        >
                          {SYNC_PRESETS.map((p) => (
                            <option key={p.months} value={p.months}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs opacity-50">
                          Changelog data before this date won&apos;t be imported. A longer range gives richer metrics
                          but takes more time on the first sync.
                        </p>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="opacity-60">Could not load projects.</div>
                )}
              </>
            )}

            {/* Step: jira-syncing / trello-syncing */}
            {(step === 'jira-syncing' || step === 'trello-syncing') && (
              <div className="space-y-10">
                <div className="space-y-3">
                  <span className="loading loading-ring loading-lg text-primary" />
                  <h1 className="text-3xl font-bold font-display">
                    Syncing your {step === 'jira-syncing' ? 'Jira' : 'Trello'} data
                  </h1>
                  <p className="text-base opacity-60">{activeStepId ? getStepLabel(activeStepId) : 'Starting sync…'}</p>
                </div>

                {/* Did you know */}
                <div
                  className="rounded-xl bg-base-200 shadow-card p-6 space-y-2 transition-opacity duration-400"
                  style={{ opacity: tipVisible ? 1 : 0 }}
                >
                  <p className="text-xs font-semibold uppercase tracking-widest opacity-40">Did you know?</p>
                  <p className="font-semibold text-base">{tip.title}</p>
                  <p className="text-sm opacity-60 leading-relaxed">{tip.body}</p>
                  <div className="flex gap-1 pt-1">
                    {DID_YOU_KNOW.map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 rounded-full transition-all duration-300 ${i === tipIndex ? 'bg-primary w-4' : 'bg-base-300 w-1.5'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step: wizard */}
            {step === 'wizard' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold font-display">Tell us what you&apos;re building</h1>
                  <p className="text-base opacity-60">
                    A sentence is enough — mFlow will propose a methodology and task breakdown you can review before
                    anything is created.
                  </p>
                </div>

                <WizardPanel
                  presetPlatform={platform}
                  onBuilt={(proposal) => {
                    setBuiltProposal(proposal);
                    setStep('done');
                  }}
                />
              </div>
            )}

            {/* Step: done */}
            {step === 'done' && (
              <div className="space-y-6">
                <LuCircleCheck className="text-6xl text-success" />
                <div className="space-y-2">
                  {builtProposal ? (
                    <>
                      <h1 className="text-3xl font-bold font-display">
                        {builtProposal.projectName || 'Your project'} is ready
                      </h1>
                      <p className="text-base opacity-60">
                        Built on <b className="capitalize">{builtProposal.platform}</b>
                        {builtProposal.builtProjectId ? ` — ${builtProposal.builtProjectId}` : ''}. Keep chatting to add
                        or change tasks anytime.
                      </p>
                    </>
                  ) : (
                    <>
                      <h1 className="text-3xl font-bold font-display">Your data is ready</h1>
                      <p className="text-base opacity-60">
                        Head to the dashboard and hit <b>Ask mFlow</b> to get your first insight.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-8 py-5 border-t border-base-300">
        <button className="btn btn-ghost btn-sm" onClick={() => dismiss()}>
          Skip setup
        </button>

        <div>
          {step === 'jira-projects' && userProjects && userProjects.availableProjects?.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleStartSync}
              disabled={isSyncing || pendingSelected.size === 0 || overLimit}
            >
              {isSyncing && <span className="loading loading-spinner loading-sm" />}
              Start Sync
            </button>
          )}
          {(step === 'jira-syncing' || step === 'trello-syncing') && (
            <button className="btn btn-ghost btn-sm" onClick={() => dismiss()}>
              Dismiss — I&apos;ll check back later
            </button>
          )}
          {step === 'done' && (
            <button className="btn btn-primary" onClick={builtProposal ? goToChat : goToDashboard}>
              {builtProposal ? 'Continue in chat' : 'Go to dashboard'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OnboardingFlow;
